import { type GetClusterInferenceMetricsRequest } from '@qdrant/qdrant-cloud-public-api/monitoring/v1';
import { createFileRoute, lazyRouteComponent, notFound, redirect } from '@tanstack/react-router';
import { monitoringService } from '@/api/services/monitoring';
import { getGroupedModelsAndDefault } from '@/components/Clusters/ClusterInference/Tab/inferenceModelsUtils';
import { type DeepOmitProtobufInternals } from '@/connect-query';
import { resolveInference } from '@/router/resolvers/inferenceResolver';
import { isInferenceAvailable } from '@/utils/cluster-utils';

export const Route = createFileRoute(
  '/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId/_tabs/inference',
)({
  beforeLoad: async ({ context: { queryClient, clusterQuery }, params: { accountId, clusterId }, matches }) => {
    const { inferenceModels, inferenceModelsQuery } = await resolveInference({
      queryClient,
      clusterQuery,
      accountId,
    });

    // Only redirect if this is the exact /inference route (not a child route)
    const isExactMatch = matches.at(-1)?.routeId === Route.id;
    if (isExactMatch && isInferenceAvailable(inferenceModels)) {
      const { defaultModel } = getGroupedModelsAndDefault(inferenceModels);
      if (!defaultModel) {
        throw notFound();
      }

      throw redirect({
        to: '/accounts/$accountId/clusters/$clusterId/inference/$inferenceModelId',
        params: { accountId, clusterId, inferenceModelId: defaultModel.id },
        replace: true,
      });
    }

    return {
      // The `$inferenceModelId` child validates its param against these instead of resolving them again.
      inferenceModels,
      inferenceModelsQuery,
      getClusterInferenceMetricsQuery: (
        query: DeepOmitProtobufInternals<Omit<GetClusterInferenceMetricsRequest, 'accountId' | 'clusterId'>>,
      ) =>
        monitoringService.getClusterInferenceMetricsQuery({
          accountId,
          clusterId,
          ...query,
        }),
    };
  },
  component: lazyRouteComponent(() => import('./inference.component'), 'ClusterInferenceTabComponent'),
});
